import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlightCheckbox, FlightDropdown, FlightTooltip, getIcon } from '@flybits/design-system';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import useCtxDataFiles, { formatCompletedTimestamp } from 'hooks/useCtxDataFiles';
import { EVENT_KEYS } from 'types/events';
import { CtxDataFile } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import UploadContextDataIcon from 'pages/ExperienceCanvas/assets/icons/UploadContextDataIcon';
import CircleCheckIcon from 'pages/ExperienceCanvas/assets/icons/CircleCheckIcon';
import { ACTIVE_STATUSES } from 'store/journey/journey.type';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import PlayIcon from '../assets/PlayIcon';
import PauseIcon from '../assets/PauseIcon';
import RefreshIcon from '../assets/RefreshIcon';
import QueueRingIcon from '../assets/QueueRingIcon';
import ProcessingSpinnerIcon from '../assets/ProcessingSpinnerIcon';
import FileIcon from '../assets/FileIcon';
import { useFileActionRunner } from '../FileActionRunnerContext';
import {
  PROCESSING_QUEUED,
  STATUS_LABELS,
  STATUS_PRIORITY,
  StatusGroup,
  classifyStatus,
  formatErrorCount,
  formatPartialFailureSummary,
  getTriggerTooltipText,
  isFailedStatus,
  isSelectableGroup,
  isTerminalFileStatus,
} from '../fileStatusGrouping';
import './FlowFileUploadDropdown.scss';

type TFlowFileUploadDropdown = {
  title: string;
  onViewAllClick: () => void;
};

const MAIN_CLASS = 'flow-file-upload-dropdown';
const DROPDOWN_PAGE_SIZE = 10;

const CLASSES = {
  COLLAPSED_TRIGGER: `${MAIN_CLASS}__collapsed-trigger`,
  TRIGGER_TITLE: `${MAIN_CLASS}__trigger-title`,
  TRIGGER_ACTIONS: `${MAIN_CLASS}__trigger-actions`,
  PILL: `${MAIN_CLASS}__pill`,
  PILL_LOCKED: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--locked`,
  PILL_READY: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--ready`,
  PILL_WARNING: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--warning`,
  PILL_PROCESSING: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--processing`,
  PILL_PAUSED: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--paused`,
  PILL_COMPLETED: `${MAIN_CLASS}__pill ${MAIN_CLASS}__pill--completed`,
  CHEVRON: `${MAIN_CLASS}__chevron`,
  DROPDOWN: `${MAIN_CLASS}__dropdown`,
  DROPDOWN_CONTENT: `${MAIN_CLASS}__dropdown-content`,
  DROPDOWN_META: `${MAIN_CLASS}__dropdown-meta`,
  DROPDOWN_META_TIMER: `${MAIN_CLASS}__dropdown-meta__timer`,
  DROPDOWN_META_REFRESH: `${MAIN_CLASS}__dropdown-meta__refresh`,
  DROPDOWN_COMPLETED: `${MAIN_CLASS}__dropdown-completed`,
  BODY: `${MAIN_CLASS}__body`,
  DRAFT_BANNER: `${MAIN_CLASS}__draft-banner`,
  PAUSED_BANNER: `${MAIN_CLASS}__paused-banner`,
  READY_BANNER: `${MAIN_CLASS}__ready-banner`,
  INFO_BANNER: `${MAIN_CLASS}__info-banner`,
  ACTION_BAR: `${MAIN_CLASS}__action-bar`,
  ACTION_BTN: `${MAIN_CLASS}__action-btn`,
  ACTION_BTN_PRIMARY: `${MAIN_CLASS}__action-btn--primary`,
  ACTION_BTN_PAUSE: `${MAIN_CLASS}__action-btn--pause`,
  FILE_LIST: `${MAIN_CLASS}__file-list`,
  GROUP: `${MAIN_CLASS}__group`,
  GROUP_HEADER: `${MAIN_CLASS}__group-header`,
  FILE: `${MAIN_CLASS}__file`,
  FILE_NAME: `${MAIN_CLASS}__file__name`,
  FILE_WITH_CHECKBOX: `${MAIN_CLASS}__file-with-checkbox`,
  EMPTY_STATE: `${MAIN_CLASS}__empty-state`,
  FOOTER: `${MAIN_CLASS}__footer`,
  VIEW_ALL_LINK: `${MAIN_CLASS}__view-all-link`,
  LOADING_ICON: `${MAIN_CLASS}__loading-icon`,
};

const FlowFileUploadDropdown: React.FC<TFlowFileUploadDropdown> = ({ title, onViewAllClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const lastFetchTimeRef = useRef<number>(Date.now());
  const journeyStatus = useSelector((state) => state.te.journey.status);
  const isJourneyActive = ACTIVE_STATUSES.includes(journeyStatus);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { localFileStatuses, localFileErrors, isRunning, inFlightFileIds, setInFlightFileIds, runByIds } =
    useFileActionRunner();

  // Share the same query key as FlowFileUploadHistory ({ limit: 1000 }) so
  // both surfaces always render from the same cached result and stay in sync
  // when the slideout opens/closes or after pause/resume actions.
  const {
    files: rawFiles,
    counts,
    pagination,
    dataUpdatedAt,
    refetch,
  } = useCtxDataFiles({
    limit: 1000,
  });

  const getEffectiveStatus = useCallback(
    (file: CtxDataFile) => {
      if (isTerminalFileStatus(file.status)) {
        return file.status;
      }
      return localFileStatuses[file.id] ?? file.status;
    },
    [localFileStatuses],
  );

  const sortedAllFiles = useMemo(
    () =>
      [...rawFiles].sort((a, b) => {
        const pa = STATUS_PRIORITY[getEffectiveStatus(a)] ?? 99;
        const pb = STATUS_PRIORITY[getEffectiveStatus(b)] ?? 99;
        if (pa !== pb) return pa - pb;
        return b.createdAt - a.createdAt;
      }),
    [rawFiles, getEffectiveStatus],
  );

  // Group counts, selection bookkeeping, and pill totals all run against the
  // full journey list so they match what the slideout shows. The rendered
  // list slices each group to DROPDOWN_PAGE_SIZE so the dropdown stays compact.
  const globalCounts = useMemo(() => {
    let processing = 0;
    let paused = 0;
    let ready = 0;
    let processed = 0;
    let failed = 0;
    sortedAllFiles.forEach((f) => {
      const s = getEffectiveStatus(f);
      if (s === FILE_STATUS.PROCESSING || s === PROCESSING_QUEUED) processing += 1;
      else if (s === FILE_STATUS.PROCESSING_PAUSED) paused += 1;
      else if (s === FILE_STATUS.UPLOADED) ready += 1;
      else if (s === FILE_STATUS.PROCESSING_SUCCESS) processed += 1;
      else if (isFailedStatus(s)) failed += 1;
    });
    return { processing, paused, ready, processed, failed };
  }, [sortedAllFiles, getEffectiveStatus]);

  useEffect(() => {
    if (dataUpdatedAt) {
      lastFetchTimeRef.current = dataUpdatedAt;
      setSecondsAgo(0);
    }
  }, [dataUpdatedAt]);

  const handleRefreshNow = useCallback(() => {
    refetch();
  }, [refetch]);

  // Clear the in-flight spinner once the server-truth status moves out of the
  // processing/queued bucket — i.e., the BE has actually advanced the file.
  useEffect(() => {
    setInFlightFileIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      rawFiles.forEach((f) => {
        if (!next.has(f.id)) return;
        if (f.status !== FILE_STATUS.PROCESSING && f.status !== FILE_STATUS.PROCESSING_QUEUED) {
          next.delete(f.id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [rawFiles, setInFlightFileIds]);

  // Drop selections that are no longer on the current page so the action-bar
  // counts always reflect what the user can see.
  useEffect(() => {
    setSelectedFileIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(rawFiles.map((f) => f.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [rawFiles]);

  const handleToggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  // Drive the processing/completed surfaces off the effective (optimistic)
  // status, not the raw server counts. After a Process/Resume/Retry action
  // resolves, `isRunning` flips back to false while the server still reports
  // the pre-action status until the next refetch — reading server counts here
  // makes the info banner and completed header flicker off during that window.
  const showProcessingUi = globalCounts.processing > 0 || isRunning;
  const showCompletedUi = sortedAllFiles.length > 0 && globalCounts.processed === sortedAllFiles.length;

  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastFetchTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const filesByGroup = useMemo(() => {
    const byGroup: Record<StatusGroup, CtxDataFile[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    sortedAllFiles.forEach((f) => {
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) byGroup[g].push(f);
    });
    return byGroup;
  }, [sortedAllFiles, getEffectiveStatus]);

  const selectedByGroup = useMemo(() => {
    const groups: Record<StatusGroup, string[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    sortedAllFiles.forEach((f) => {
      if (!selectedFileIds.has(f.id)) return;
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) groups[g].push(f.id);
    });
    return groups;
  }, [sortedAllFiles, selectedFileIds, getEffectiveStatus]);

  const failedFiles = filesByGroup.failed;
  const readyFiles = filesByGroup.ready;
  const hasFailedFiles = failedFiles.length > 0;
  const processingTotal = pagination?.totalRecords ?? counts.total;
  const processingProcessedCount = globalCounts.processed;
  const allInFlightPaused = filesByGroup.processing.length === 0 && filesByGroup.paused.length > 0;
  // Files still waiting to finish (paused + anything queued/processing) — the
  // "X files remain in the queue" count shown in the paused banner.
  const pausedQueueCount = globalCounts.paused + globalCounts.processing;

  const completedTimestamp = useMemo(() => {
    if (!showCompletedUi || !sortedAllFiles.length) return '';
    const latestCompletedAt = Math.max(...sortedAllFiles.map((f) => f.completedAt || 0));
    return latestCompletedAt > 0 ? formatCompletedTimestamp(latestCompletedAt) : '';
  }, [showCompletedUi, sortedAllFiles]);

  // When the journey is inactive, processing is locked for every file
  // regardless of their individual statuses (ready, paused, failed), so the
  // "launch to unlock" draft banner and locked pill apply to any non-empty
  // list — not only when all files are ready.
  const isDraftUploadedState = !isJourneyActive && sortedAllFiles.length > 0;
  const isReadyToProcessState = readyFiles.length > 0 && readyFiles.length === sortedAllFiles.length && isJourneyActive;

  // Heading for the ready/uploaded group reflects the journey + processing
  // lifecycle: locked draft → "Uploaded files", active & idle →
  // "X unprocessed files", in-flight → "Processing X files", all done →
  // "X files processed".
  const readyGroupHeading = (count: number): string => {
    if (!isJourneyActive) return 'Uploaded files';
    if (showCompletedUi) return `${processingProcessedCount} files processed`;
    return `${count} unprocessed files`;
  };

  const tooltipText = getTriggerTooltipText({
    files: sortedAllFiles,
    counts,
    processed: processingProcessedCount,
    failed: globalCounts.failed,
    ready: globalCounts.ready,
    total: processingTotal,
    showProcessingUi,
  });

  const openDropdown = () => setIsOpen(true);
  const closeDropdown = (focusAfterClose = false) => {
    setIsOpen(false);
    if (focusAfterClose) triggerRef.current?.focus();
  };
  const handleViewAllClick = () => {
    closeDropdown();
    onViewAllClick();
  };

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === EVENT_KEYS.ESCAPE) closeDropdown(true);
    };
    document.addEventListener('keyup', handleKeyUp);
    return () => document.removeEventListener('keyup', handleKeyUp);
  }, []);

  const dispatchAction = async (group: StatusGroup, fileIds: string[]) => {
    if (fileIds.length === 0) return;
    switch (group) {
      case 'processing':
        await runByIds({ action: 'PAUSE', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_PAUSED });
        break;
      case 'paused':
        await runByIds({ action: 'RESUME', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_QUEUED });
        break;
      case 'ready':
        await runByIds({ action: 'PROCESS', fileIds, optimisticStatus: FILE_STATUS.PROCESSING });
        break;
    }
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      fileIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const renderPill = () => {
    if (isDraftUploadedState) {
      return (
        <span className={CLASSES.PILL_LOCKED}>
          Process file(s)
          {getIcon('lock', {})}
        </span>
      );
    }
    if (isReadyToProcessState) {
      return (
        <span className={CLASSES.PILL_READY}>
          Ready to process
          <PlayIcon fill="#505a72" ariaHidden />
        </span>
      );
    }
    const showReadyWithFailed =
      readyFiles.length > 0 && hasFailedFiles && !showProcessingUi && !showCompletedUi && globalCounts.processed === 0;
    if (showReadyWithFailed) {
      return (
        <span className={CLASSES.PILL_READY}>
          Ready to process
          <PlayIcon fill="#505a72" ariaHidden />
        </span>
      );
    }
    const showPartialFailurePill =
      hasFailedFiles &&
      !showProcessingUi &&
      !showCompletedUi &&
      (globalCounts.processed > 0 || readyFiles.length === 0);
    if (showPartialFailurePill) {
      return (
        <span className={CLASSES.PILL_WARNING}>
          {formatPartialFailureSummary(processingProcessedCount, processingTotal, globalCounts.failed)}
          {getIcon('error', { fill: '#ff8800' })}
        </span>
      );
    }
    if (allInFlightPaused && !showProcessingUi) {
      return (
        <span className={CLASSES.PILL_PAUSED}>
          Processing paused
          <PauseIcon ariaHidden />
        </span>
      );
    }
    if (showProcessingUi) {
      const hasErrorsDuringProcessing = globalCounts.failed > 0;
      const pillLabel = hasErrorsDuringProcessing
        ? `Processing (${processingProcessedCount}/${processingTotal}) - ${formatErrorCount(globalCounts.failed)}`
        : `Processing (${processingProcessedCount}/${processingTotal})`;
      return (
        <span className={hasErrorsDuringProcessing ? CLASSES.PILL_WARNING : CLASSES.PILL_PROCESSING}>
          {pillLabel}
          <ProcessingSpinnerIcon ariaHidden />
        </span>
      );
    }
    if (showCompletedUi) {
      return (
        <span className={CLASSES.PILL_COMPLETED}>
          Completed
          <CircleCheckIcon fill="#006117" />
        </span>
      );
    }
    return null;
  };

  const triggerButton = (
    <button
      type="button"
      ref={triggerRef}
      onClick={openDropdown}
      onKeyUp={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
        if (evt.key === EVENT_KEYS.ENTER) openDropdown();
      }}
    >
      <UploadContextDataIcon />
      <span className={CLASSES.TRIGGER_TITLE}>{title}</span>
      <span className={CLASSES.TRIGGER_ACTIONS}>
        {renderPill()}
        <span className={CLASSES.CHEVRON} aria-hidden>
          {getIcon(isOpen ? 'baselineKeyboardArrowDown' : 'baselineKeyboardArrowRight', {})}
        </span>
      </span>
    </button>
  );

  const renderFileRow = (file: CtxDataFile) => {
    const effectiveStatus = getEffectiveStatus(file);
    const errorMessage = localFileErrors[file.id];
    const isInFlight = inFlightFileIds.has(file.id);

    const statusIcon = () => {
      if (isInFlight) {
        return (
          <span className={CLASSES.LOADING_ICON}>
            <LoadingIcon />
          </span>
        );
      }
      if (errorMessage) {
        return (
          <FlightTooltip description={errorMessage} direction="top">
            <span className={CLASSES.LOADING_ICON}>{getIcon('error', { fill: '#dc3616' })}</span>
          </FlightTooltip>
        );
      }
      if (effectiveStatus === FILE_STATUS.PROCESSING_SUCCESS) {
        return <CircleCheckIcon fill="#006117" />;
      }
      if (effectiveStatus === FILE_STATUS.PROCESSING) {
        return (
          <span className={CLASSES.LOADING_ICON}>
            <LoadingIcon />
          </span>
        );
      }
      if (effectiveStatus === PROCESSING_QUEUED) {
        return <QueueRingIcon ariaHidden />;
      }
      if (effectiveStatus === FILE_STATUS.PROCESSING_PAUSED) {
        return <PauseIcon ariaHidden />;
      }
      if (isFailedStatus(effectiveStatus)) {
        const errorDetails = file.statusMessage || STATUS_LABELS[effectiveStatus] || 'Failed';
        return (
          <FlightTooltip description={errorDetails} direction="top">
            <span className={CLASSES.LOADING_ICON}>{getIcon('error', { fill: '#dc3616' })}</span>
          </FlightTooltip>
        );
      }
      return <FileIcon ariaHidden />;
    };

    const group = classifyStatus(effectiveStatus);
    const checkboxAllowed = isJourneyActive && isSelectableGroup(group);

    if (checkboxAllowed) {
      return (
        <div key={file.id} className={CLASSES.FILE_WITH_CHECKBOX}>
          <FlightCheckbox
            checkState={selectedFileIds.has(file.id) ? 'SELECTED' : 'UNSELECTED'}
            onSelect={() => {
              if (isRunning) return;
              handleToggleFileSelection(file.id);
            }}
            disabled={isRunning}
          />
          {statusIcon()}
          <span className={CLASSES.FILE_NAME}>{file.name}</span>
        </div>
      );
    }

    return (
      <div key={file.id} className={CLASSES.FILE}>
        {statusIcon()}
        <span className={CLASSES.FILE_NAME}>{file.name}</span>
      </div>
    );
  };

  const renderActionBtn = (
    key: string,
    label: string,
    onClick: () => void,
    variant: 'primary' | 'pause' = 'primary',
    isProcessAction = false,
  ) => (
    <button
      key={key}
      type="button"
      className={`${CLASSES.ACTION_BTN} ${variant === 'pause' ? CLASSES.ACTION_BTN_PAUSE : CLASSES.ACTION_BTN_PRIMARY}`}
      onClick={onClick}
      disabled={isRunning}
      aria-busy={(isProcessAction && isRunning) || undefined}
    >
      <span>{label}</span>
      {isProcessAction && isRunning ? (
        <span className={CLASSES.LOADING_ICON}>
          <LoadingIcon />
        </span>
      ) : variant === 'pause' ? (
        <PauseIcon ariaHidden />
      ) : (
        <PlayIcon ariaHidden />
      )}
    </button>
  );

  // Action button(s) that belong inline with a group header. Each group surfaces
  // only the action that applies to its own status, so the user always sees the
  // control next to the rows it affects.
  const renderGroupAction = (groupKey: StatusGroup): React.ReactNode => {
    const groupFiles = filesByGroup[groupKey];
    const selectedIds = selectedByGroup[groupKey];

    if (groupKey === 'processing') {
      if (!isJourneyActive) return null;
      if (selectedIds.length > 0) {
        return renderActionBtn(
          'pause-sel',
          `Pause selected files (${selectedIds.length})`,
          () => dispatchAction('processing', selectedIds),
          'pause',
        );
      }
      if (groupFiles.length === 0) return null;
      return renderActionBtn(
        'pause-all',
        'Pause',
        () =>
          dispatchAction(
            'processing',
            groupFiles.map((f) => f.id),
          ),
        'pause',
      );
    }

    if (groupKey === 'paused') {
      if (!isJourneyActive) return null;
      if (selectedIds.length > 0) {
        return renderActionBtn('resume-sel', `Resume selected files (${selectedIds.length})`, () =>
          dispatchAction('paused', selectedIds),
        );
      }
      if (groupFiles.length === 0) return null;
      return renderActionBtn('resume-all', 'Resume', () =>
        dispatchAction(
          'paused',
          groupFiles.map((f) => f.id),
        ),
      );
    }

    if (groupKey === 'ready') {
      if (!isJourneyActive) return null;
      if (selectedIds.length > 0) {
        return renderActionBtn(
          'process-sel',
          `Process selected files (${selectedIds.length})`,
          () => dispatchAction('ready', selectedIds),
          'primary',
          true,
        );
      }
      if (groupFiles.length === 0) return null;
      return renderActionBtn(
        'process-all',
        'Process',
        () =>
          dispatchAction(
            'ready',
            groupFiles.map((f) => f.id),
          ),
        'primary',
        true,
      );
    }

    return null;
  };

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.COLLAPSED_TRIGGER}>
        <FlightDropdown
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          className={CLASSES.DROPDOWN}
          maxWidth="388px"
          isActive={isOpen}
          trigger={
            tooltipText ? (
              <FlightTooltip direction="bottom" description={tooltipText} isEnabled={!isOpen}>
                {triggerButton}
              </FlightTooltip>
            ) : (
              triggerButton
            )
          }
          isControlledByIsActive
          handleClickOutside={() => closeDropdown()}
        >
          <div className={CLASSES.DROPDOWN_CONTENT}>
            {isJourneyActive && !showCompletedUi && (showProcessingUi || allInFlightPaused) && (
              <div className={CLASSES.DROPDOWN_META}>
                <span className={CLASSES.DROPDOWN_META_TIMER}>
                  Last updated:
                  <span>
                    {secondsAgo} second{secondsAgo !== 1 ? 's' : ''} ago
                  </span>
                </span>
                <button
                  type="button"
                  className={CLASSES.DROPDOWN_META_REFRESH}
                  onClick={handleRefreshNow}
                  aria-label="Refresh now"
                >
                  Refresh now <RefreshIcon ariaHidden />
                </button>
              </div>
            )}
            {showCompletedUi && (
              <div className={CLASSES.DROPDOWN_COMPLETED}>
                Completed:{completedTimestamp && <span>{completedTimestamp}</span>}
              </div>
            )}
            <div className={CLASSES.BODY}>
              {isDraftUploadedState && (
                <div className={CLASSES.DRAFT_BANNER}>
                  {getIcon('infoFilled', {})}
                  <div>
                    <strong>Launch this experience to unlock file processing</strong>
                    Once you launch this experience, you will be able to process your uploaded files
                  </div>
                </div>
              )}
              {isJourneyActive && isReadyToProcessState && (
                <div className={CLASSES.READY_BANNER}>
                  {getIcon('infoFilled', {})}
                  <div>
                    <strong>Confirm your selection</strong>
                    Once processing begins, the queue order is final and files cannot be removed.
                  </div>
                </div>
              )}
              {isJourneyActive && showProcessingUi && (
                <div className={CLASSES.INFO_BANNER}>
                  {getIcon('infoFilled', {})}
                  <div>
                    <strong>Processing one by one.</strong>
                    You&rsquo;re all set! Your files are queued and will start automatically.
                  </div>
                </div>
              )}
              {isJourneyActive && !showProcessingUi && allInFlightPaused && (
                <div className={CLASSES.PAUSED_BANNER}>
                  {getIcon('infoFilled', {})}
                  <div>
                    <strong>Processing is currently paused.</strong>
                    {pausedQueueCount} file{pausedQueueCount !== 1 ? 's' : ''} remain in the queue
                  </div>
                </div>
              )}

              {sortedAllFiles.length > 0 ? (
                <div className={CLASSES.FILE_LIST}>
                  {(
                    [
                      ['processing', 'Processing'],
                      ['paused', 'Paused'],
                      ['ready', 'Ready to process'],
                    ] as [StatusGroup, string][]
                  ).map(([groupKey, groupLabel]) => {
                    const groupFiles = filesByGroup[groupKey];
                    if (groupFiles.length === 0) return null;
                    // Render only the first slice within each group to keep the
                    // dropdown compact; the count in the header reflects the
                    // full journey total so users know "View all" has more.
                    const visible = groupFiles.slice(0, DROPDOWN_PAGE_SIZE);
                    // The ready/uploaded group carries the lifecycle-aware
                    // heading; the other status groups keep their static
                    // "Label (count)" form.
                    const headerLabel =
                      groupKey === 'ready'
                        ? readyGroupHeading(groupFiles.length)
                        : `${groupLabel} (${groupFiles.length})`;
                    return (
                      <div key={groupKey} className={CLASSES.GROUP}>
                        <div className={CLASSES.GROUP_HEADER}>
                          <span>{headerLabel}</span>
                          {renderGroupAction(groupKey)}
                        </div>
                        {visible.map(renderFileRow)}
                      </div>
                    );
                  })}
                  {(() => {
                    const processedFiles = sortedAllFiles.filter(
                      (f) => getEffectiveStatus(f) === FILE_STATUS.PROCESSING_SUCCESS,
                    );
                    if (processedFiles.length === 0) return null;
                    const visible = processedFiles.slice(0, DROPDOWN_PAGE_SIZE);
                    return (
                      <div key="processed" className={CLASSES.GROUP}>
                        <div className={CLASSES.GROUP_HEADER}>
                          <span>Processed ({processedFiles.length})</span>
                        </div>
                        {visible.map(renderFileRow)}
                      </div>
                    );
                  })()}
                  {failedFiles.length > 0 && (
                    <div key="failed" className={CLASSES.GROUP}>
                      <div className={CLASSES.GROUP_HEADER}>
                        <span>Failed ({failedFiles.length})</span>
                      </div>
                      {failedFiles.slice(0, DROPDOWN_PAGE_SIZE).map(renderFileRow)}
                    </div>
                  )}
                </div>
              ) : (
                <div className={CLASSES.EMPTY_STATE}>No context data file yet</div>
              )}
            </div>
            <div className={CLASSES.FOOTER}>
              <button
                type="button"
                className={CLASSES.VIEW_ALL_LINK}
                onClick={handleViewAllClick}
                onKeyDown={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (evt.key === EVENT_KEYS.ENTER) handleViewAllClick();
                }}
              >
                Manage all files
              </button>
            </div>
          </div>
        </FlightDropdown>
      </div>
    </div>
  );
};

export default FlowFileUploadDropdown;
